import { useEffect, useMemo, useRef, useState, type FocusEvent } from 'react';
import { styled } from '@mui/material/styles';
import CancelIcon from '@/assets/cancel2.svg?react';
import { useIntl } from 'react-intl';

export const renameFile = (file: File, newName: string): File =>
  new File([file], newName, {
    type: file.type,
    lastModified: file.lastModified,
  });

/** A separator would turn a rename into a move. */
const withoutPathSeparators = (value: string) => value.replace(/[\\/]/g, '');

export const splitFileName = (fileName: string) => {
  const dot = fileName.lastIndexOf('.');
  if (dot === -1 || dot === fileName.length - 1) {
    return { baseName: fileName, extension: '' };
  }
  return {
    baseName: fileName.slice(0, dot),
    extension: fileName.slice(dot),
  };
};

export type EditableFileNameProps = {
  fileName: string;
  disabled?: boolean;
  onSave: (newName: string) => void;
};

const EditableFileName = ({
  fileName,
  disabled = false,
  onSave,
}: EditableFileNameProps) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const { baseName, extension } = useMemo(
    () => splitFileName(fileName),
    [fileName],
  );
  const [draftBaseName, setDraftBaseName] = useState(baseName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraftBaseName(baseName);
    }
  }, [baseName, isEditing]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const buildFullName = (trimmedBase: string) =>
    extension ? `${trimmedBase}${extension}` : trimmedBase;

  const handleCancel = () => {
    setDraftBaseName(baseName);
    setIsEditing(false);
  };

  const handleSave = () => {
    const trimmedBase = draftBaseName.trim();
    if (!trimmedBase) {
      handleCancel();
      return;
    }
    const newName = buildFullName(trimmedBase);
    if (newName !== fileName) {
      onSave(newName);
    }
    setIsEditing(false);
  };

  const startEditing = () => {
    if (disabled) return;
    setDraftBaseName(baseName);
    setIsEditing(true);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const editRow = e.currentTarget.closest('[data-editable-file-name-edit]');
    const next = e.relatedTarget;
    if (next && editRow?.contains(next)) {
      return;
    }
    handleSave();
  };

  if (isEditing) {
    return (
      <NameEditRow data-editable-file-name-edit>
        <NameEditField>
          <NameInput
            ref={inputRef}
            value={draftBaseName}
            disabled={disabled}
            onChange={(e) =>
              setDraftBaseName(withoutPathSeparators(e.target.value))
            }
            onBlur={handleBlur}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label={intl.formatMessage({
              id: 'complianceDocumentsUploadModal.editFileName',
              defaultMessage: 'Edit file name',
            })}
          />
          {extension ? (
            <NameExtension aria-hidden>{extension}</NameExtension>
          ) : null}
        </NameEditField>
        <NameEditActions>
          <NameEditButton
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleCancel();
            }}
            aria-label={intl.formatMessage({
              id: 'complianceDocumentsUploadModal.cancelFileNameEdit',
              defaultMessage: 'Cancel file name edit',
            })}
          >
            <CancelIcon width={16} height={16} aria-hidden />
          </NameEditButton>
        </NameEditActions>
      </NameEditRow>
    );
  }

  return (
    <NameLabel
      role="button"
      tabIndex={disabled ? -1 : 0}
      $disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        startEditing();
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          startEditing();
        }
      }}
      title={fileName}
    >
      {fileName}
    </NameLabel>
  );
};

const nameTypography = {
  fontSize: '12px',
  lineHeight: '16px',
};

const NameLabel = styled('span')<{ $disabled: boolean }>(
  ({ theme, $disabled }) => ({
    ...nameTypography,
    display: 'block',
    color: theme.palette.textPrimaryBlack,
    // Wrap instead of truncating so the full name is readable.
    overflowWrap: 'anywhere',
    cursor: $disabled ? 'default' : 'pointer',
    opacity: $disabled ? 0.45 : 1,
  }),
);

const NameEditRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0,
  height: '16px',
}));

const NameEditField = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  gap: theme.spacing(0.25),
  height: '16px',
}));

const NameInput = styled('input')(({ theme }) => ({
  ...nameTypography,
  flex: 1,
  minWidth: 0,
  width: 0,
  height: '16px',
  margin: 0,
  padding: '0 2px',
  boxSizing: 'border-box',
  border: `1px solid ${theme.palette.grey300}`,
  borderRadius: theme.radii.xxs,
  color: theme.palette.textPrimaryBlack,
  background: theme.palette.white,
  outline: 'none',
  '&:focus': {
    borderColor: theme.palette.primary500,
  },
  '&:disabled': {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
}));

const NameExtension = styled('span')(({ theme }) => ({
  ...nameTypography,
  flexShrink: 0,
  color: theme.palette.textSecondaryDarkGrey,
  userSelect: 'none',
}));

const NameEditActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: theme.spacing(0.25),
}));

const NameEditButton = styled('button')(({ theme }) => ({
  margin: 0,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  color: theme.palette.iconBlack,
  '& path': {
    stroke: 'currentColor',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.45,
  },
}));

export default EditableFileName;
